import { localModel } from './names.js';
import { makeItem } from '../model/board.js';
import type { Catalog, CatalogEntry, Item } from '../model/schema.js';

/** Lower-case and strip everything that is not a letter or digit ("Beta 52A" → "beta52a"). */
export function normalizeToken(text: string): string {
  return text.toLowerCase().replace(/[^\p{L}\p{N}]+/gu, '');
}

/**
 * Finds an entry by `ref` across several catalogs. Later catalogs override earlier ones
 * (same precedence as {@link mergeCatalogs}): pass `[builtin, workspace]`.
 */
export function findCatalogEntry(
  catalogs: readonly Catalog[],
  ref: string,
): CatalogEntry | undefined {
  for (let i = catalogs.length - 1; i >= 0; i--) {
    const hit = catalogs[i]!.entries.find((e) => e.ref === ref);
    if (hit !== undefined) return hit;
  }
  return undefined;
}

/** Every exact token an entry answers to: ref, aliases, model, brand+model. */
export function entryTokens(entry: CatalogEntry): string[] {
  return [entry.ref, ...entry.aliases, entry.model, `${entry.brand}${entry.model}`].map(
    normalizeToken,
  );
}

/** Entry whose ref/alias/model matches `token` exactly (after normalisation), if any. */
export function matchCatalogToken(
  entries: readonly CatalogEntry[],
  token: string,
): CatalogEntry | undefined {
  const t = normalizeToken(token);
  if (t === '') return undefined;
  return entries.find((e) => entryTokens(e).includes(t));
}

/**
 * Relevance of `entry` for `query` (0 = no match). Exact alias/model hits rank highest, then
 * prefixes, then entries whose brand/model/aliases contain every query word.
 */
export function scoreCatalogEntry(entry: CatalogEntry, query: string): number {
  const q = normalizeToken(query);
  if (q === '') return 0;
  const tokens = entryTokens(entry);
  if (tokens.includes(q)) return 100;
  if (tokens.some((t) => t.startsWith(q))) return 50;
  const haystack = tokens.join(' ');
  const words = query
    .toLowerCase()
    .split(/\s+/)
    .map(normalizeToken)
    .filter((w) => w !== '');
  const kind = normalizeToken(entry.kind);
  const hits = words.filter((w) => haystack.includes(w) || kind.startsWith(w));
  return hits.length === words.length ? 10 + hits.length : 0;
}

/**
 * Ranked fuzzy-ish search over brand, model, aliases and ref. Ties keep catalog order.
 * Returns at most `limit` entries.
 */
export function searchCatalog(
  entries: readonly CatalogEntry[],
  query: string,
  limit = 20,
): CatalogEntry[] {
  return entries
    .map((entry, index) => ({ entry, index, score: scoreCatalogEntry(entry, query) }))
    .filter((m) => m.score > 0)
    .sort((a, b) => b.score - a.score || a.index - b.index)
    .slice(0, limit)
    .map((m) => m.entry);
}

/**
 * Item fields derived from a catalog entry (no id). Ports are copied with the entry's port ids
 * so connections can reference them predictably.
 */
export function itemFieldsFromCatalog(
  entry: CatalogEntry,
  locale: 'en' | 'de' = 'en',
): Partial<Item> {
  const fields: Partial<Item> = {
    // generic models get a label in the UI language ("Galgenstativ, hoch", OIL-121)
    label: localModel(entry.model, entry.brand, locale),
    catalogRef: entry.ref,
    brand: entry.brand,
    model: entry.model,
    ports: entry.ports.map((p) => ({ ...p })),
    props: { ...entry.props },
  };
  if (entry.pattern !== undefined) fields.pattern = entry.pattern;
  // chairs & co. need no "Chair (5)" on the plot (OIL-117); the label can be shown again
  if (entry.kind === 'generic' && entry.props?.['furniture'] !== undefined)
    fields.labelHidden = true;
  if (entry.power !== undefined) fields.power = { ...entry.power };
  if (entry.width !== undefined) fields.width = entry.width;
  if (entry.depth !== undefined) fields.depth = entry.depth;
  if (entry.stand !== undefined) fields.stand = { ...entry.stand };
  if (entry.truss !== undefined) fields.truss = { ...entry.truss };
  if (entry.light !== undefined)
    fields.light = {
      ...entry.light,
      ...(entry.light.dmx !== undefined ? { dmx: { ...entry.light.dmx } } : {}),
    };
  return fields;
}

/**
 * Whether an item's brand / model still describe its catalog entry (OIL-81): an item duplicated
 * from an MD 421 and retyped to "AE2300" must not keep the MD 421 reference. Missing brand / model
 * on the item do not contradict the entry; spelling (case, spaces) does not matter.
 */
export function catalogRefMatches(
  item: { brand?: string | undefined; model?: string | undefined },
  entry: CatalogEntry,
): boolean {
  const same = (a: string | undefined, b: string) =>
    a === undefined || a.trim() === '' || normalizeToken(a) === normalizeToken(b);
  return same(item.model, entry.model) && same(item.brand, entry.brand);
}

/** Items whose catalog reference contradicts their brand / model (references to unknown entries stay). */
export function staleCatalogRefs(items: readonly Item[], catalogs: readonly Catalog[]): Item[] {
  return items.filter((i) => {
    if (i.catalogRef === undefined) return false;
    const entry = findCatalogEntry(catalogs, i.catalogRef);
    return entry !== undefined && !catalogRefMatches(i, entry);
  });
}

/** Creates a new item from a catalog entry; `overrides` win over catalog values. */
export function itemFromCatalog(
  entry: CatalogEntry,
  overrides: Partial<Item> = {},
  locale: 'en' | 'de' = 'en',
): Item {
  return makeItem(entry.kind, { ...itemFieldsFromCatalog(entry, locale), ...overrides });
}

/**
 * Merges the built-in and a workspace catalog. Workspace entries replace built-ins with the same
 * `ref` in place; new workspace entries are appended in their order.
 */
export function mergeCatalogs(builtin: Catalog, workspace: Catalog | undefined): Catalog {
  const overrides = new Map((workspace?.entries ?? []).map((e) => [e.ref, e]));
  const builtinRefs = new Set(builtin.entries.map((e) => e.ref));
  const merged = builtin.entries.map((e) => overrides.get(e.ref) ?? e);
  const added = (workspace?.entries ?? []).filter((e) => !builtinRefs.has(e.ref));
  return { version: 1, entries: [...merged, ...added] };
}
